import httpClient from "../utils/http";
import { ENDPOINTS } from "../utils/endpoints";
import type {
  IAuthResponse,
  IAuthUser,
  ILoginCredentials,
  IResponse,
} from "../interfaces";

/**
 * Login user with credentials
 * @param credentials - Login credentials (email and password)
 * @returns Promise with authentication response
 */
export const login = async (
  credentials: ILoginCredentials
): Promise<IResponse<IAuthResponse>> => {
  const response = await httpClient.post<IResponse<IAuthResponse>>(
    ENDPOINTS.LOGIN,
    credentials
  );
  return response.data;
};

/**
 * Logout current user
 * @returns Promise with logout response
 */
export const logout = async (): Promise<IResponse> => {
  const response = await httpClient.post<IResponse>(ENDPOINTS.LOGOUT);
  return response.data;
};

/**
 * Refresh authentication token.
 * Sends refresh token in body (current backend) or relies on HttpOnly cookie (future backend).
 * @param token - Refresh token (omit when using cookie-based auth)
 * @returns Promise with new auth tokens
 */
export const refreshToken = async (token?: string): Promise<IResponse<IAuthResponse>> => {
  const response = await httpClient.post<IResponse<IAuthResponse>>(
    ENDPOINTS.REFRESH_TOKEN,
    token ? { refreshToken: token } : undefined
  );
  return response.data;
};

/**
 * Request password reset
 * @param email - User email
 * @returns Promise with response
 */
export const forgotPassword = async (email: string): Promise<IResponse> => {
  const response = await httpClient.post<IResponse>(ENDPOINTS.FORGOT_PASSWORD, {
    email,
  });
  return response.data;
};

/**
 * Verify OTP code
 * @param email - User email
 * @param code - Verification code
 * @returns Promise with response containing email
 */
export const verifyCode = async (
  email: string,
  code: string
): Promise<IResponse<{ email: string }>> => {
  const response = await httpClient.post<IResponse<{ email: string }>>(
    ENDPOINTS.VERIFY_OTP,
    { email, code }
  );
  return response.data;
};

/**
 * Reset password with OTP code
 * @param email - User email
 * @param code - Verification code
 * @param password - New password
 * @returns Promise with response
 */
export const resetPassword = async (
  email: string,
  code: string,
  password: string
): Promise<IResponse> => {
  const response = await httpClient.post<IResponse>(ENDPOINTS.RESET_PASSWORD, {
    email,
    code,
    password,
  });
  return response.data;
};

/**
 * Change password for authenticated user
 * @param oldPassword - Current password
 * @param newPassword - New password
 * @returns Promise with response
 */
export const changePassword = async (
  oldPassword: string,
  newPassword: string
): Promise<IResponse> => {
  const response = await httpClient.post<IResponse>(ENDPOINTS.CHANGE_PASSWORD, {
    oldPassword,
    newPassword,
  });
  return response.data;
};

/**
 * Unified update - Update profile image, name, email, or password
 * @param data - Update data object
 * @returns Promise with updated user data
 */
export const unifiedUpdate = async (data: {
  profileImage?: string;
  fullName?: string;
  email?: string;
  oldPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}): Promise<IResponse<IAuthUser>> => {
  const response = await httpClient.put<IResponse<IAuthUser>>(
    ENDPOINTS.UNIFIED_UPDATE,
    data
  );
  return response.data;
};
